"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { MessageSquare, Star } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { showSiteToast } from "./site-toast";
import styles from "./product-info-tabs.module.css";

type Review = { id: number; authorName: string; rating: number; comment: string; verified: boolean; createdAt: string };

export function ProductReviews({ slug, active }: { slug: string; active: boolean }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadError, setLoadError] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [checking, setChecking] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    if (startedAt.current === null) startedAt.current = Date.now();
    let mounted = true;
    const load = async () => {
      const configured = Boolean(process.env.NEXT_PUBLIC_SUPABASE_URL && process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY);
      if (configured) {
        try {
          const { data } = await createClient().auth.getUser();
          if (mounted) setSignedIn(Boolean(data.user));
        } catch { if (mounted) setSignedIn(false); }
      }
      if (mounted) setChecking(false);
      try {
        const response = await fetch(`/api/reviews?slug=${encodeURIComponent(slug)}`, { cache: "no-store" });
        const data: { reviews?: Review[]; error?: string } = await response.json();
        if (!response.ok) throw new Error(data.error || "دریافت دیدگاه‌ها ممکن نشد.");
        if (mounted) { setReviews(data.reviews || []); setLoadError(""); }
      } catch { if (mounted) setLoadError("دریافت دیدگاه‌ها موقتاً ممکن نشد."); }
    };
    void load();
    return () => { mounted = false; };
  }, [active, slug]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!rating) { showSiteToast("برای دیدگاهتان امتیاز انتخاب کنید.", "error"); return; }
    setBusy(true);
    try {
      const data = new FormData(event.currentTarget);
      const response = await fetch("/api/reviews", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, rating, comment, website: data.get("website"), startedAt: startedAt.current }),
      });
      const result: { message?: string; error?: string } = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "ثبت دیدگاه انجام نشد.");
      const message = result.message || "دیدگاه شما ثبت شد و پس از بررسی نمایش داده می‌شود.";
      showSiteToast(message, "success");
      setRating(0);
      setComment("");
      startedAt.current = Date.now();
    } catch (error) {
      showSiteToast(error instanceof Error ? error.message : "ثبت دیدگاه انجام نشد.", "error");
    } finally { setBusy(false); }
  }

  return <div className={styles.reviewsContent} id="reviews">
    {loadError && <p className={styles.reviewNotice} role="status">{loadError}</p>}
    {reviews.length ? <div className={styles.reviewList} role="list" aria-label="دیدگاه‌های تأییدشده">
      {reviews.map((review) => <article className={styles.reviewCard} role="listitem" key={review.id}>
        <div className={styles.reviewAvatar} aria-hidden="true">{review.authorName.slice(0, 1)}</div>
        <div><div className={styles.reviewCardHead}><strong>{review.authorName}</strong><span>{new Intl.DateTimeFormat("fa-IR", { dateStyle: "medium" }).format(new Date(review.createdAt))}</span></div>
          <div className={styles.reviewStars} role="img" aria-label={`${review.rating} از ۵ ستاره`}>{"★".repeat(review.rating)}<span aria-hidden="true">{"☆".repeat(5 - review.rating)}</span></div>
          <p>{review.comment}</p>{review.verified && <small>خریدار تأییدشده</small>}
        </div>
      </article>)}
    </div> : !loadError && <div className="reviews-empty">
      <MessageSquare size={28} aria-hidden="true" />
      <span className="detail-rating-stars" aria-hidden="true">{Array.from({ length: 5 }, (_, index) => <Star key={index} size={19} />)}</span>
      <strong>هنوز دیدگاه تأییدشده‌ای برای این محصول نیست.</strong>
    </div>}
    <div className={styles.reviewComposer}>
      <h3>دیدگاه شما</h3>
      {checking ? <p>وضعیت حساب در حال بررسی است…</p> : signedIn ? <form onSubmit={submit}>
        <fieldset className={styles.ratingField}><legend>امتیاز شما</legend>
          {[1, 2, 3, 4, 5].map((value) => <label key={value} className={rating >= value ? styles.selectedStar : ""}>
            <input type="radio" name="rating" value={value} checked={rating === value} onChange={() => setRating(value)} required />
            <span aria-hidden="true">★</span><span className="sr-only">{value} ستاره</span>
          </label>)}
        </fieldset>
        <label htmlFor="review-comment">متن دیدگاه</label>
        <textarea id="review-comment" value={comment} onChange={(event) => setComment(event.target.value)} minLength={15} maxLength={1200} rows={4} required placeholder="تجربهٔ خودتان از این محصول را بنویسید." />
        <input className={styles.honeypot} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <p>دیدگاه پس از بررسی در همین صفحه نمایش داده می‌شود.</p>
        <button className="button primary" type="submit" disabled={busy}>{busy ? "در حال ثبت…" : "ثبت دیدگاه"}</button>
      </form> : <p>برای ثبت دیدگاه، <Link href="/account">وارد حساب کاربری</Link> شوید.</p>}
    </div>
  </div>;
}
